/**
 * useCompleteTask - the ONE way a task gets completed (T-04 / T-09), shared
 * by the account Tasks panel, the opportunity/project Tasks tab, the Tasks
 * page and the Dashboard cards.
 *
 * Ticking a task opens "Complete task" (CompleteTaskDialog) with an optional
 * note that goes on the timeline (T-09). Pressing Complete then:
 *   - on the LAST open task of a lead (Active or Cold) or an open opportunity,
 *     asks "What's the next step?" (NextStepPrompt) instead of completing. The
 *     prompt can only be left by saving the next task, setting the lead
 *     Inactive or Dead, or closing the opportunity (Won / Lost / Cancelled /
 *     No Quote) - and only then is the task marked complete, with the note.
 *     Cancel changes nothing; the task stays open.
 *   - otherwise completes it straight away.
 * So the order is always note first, then the prompt. Closing as Won then
 * opens the Won project setup (PO form, WonProjectSetup) - Paul Q-02, every
 * route to Won asks for the PO.
 *
 * The PUT body is exactly { status: 'complete' }, plus `note` only when one
 * was typed. The GLOBAL KSA tab has no leads and no timeline, so there it is
 * a plain completion and the box has no note field.
 *
 *   const { complete, prompt } = useCompleteTask();
 *   ... onClick={() => complete(task)} ... and render {prompt} anywhere.
 */
import { createElement, useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../../context/AppContext';
import { useApiBase, useDataScope } from '../../context/DataScope';
import { homeNeedsTask, homeOfTask, indexById, nextStepHome } from '../crm/nextStep';
import CompleteTaskDialog, { NOTE_MAX, TaskNotice } from './CompleteTaskDialog';
import NextStepPrompt from './NextStepPrompt';
import { notifyTasksChanged, TASK_ENTITY } from './useTasks';
import WonProjectSetup from '../opportunities/WonProjectSetup';

const JSON_HEADERS = { 'Content-Type': 'application/json' };
const errorOf = async (r, fallback) => {
  if (r && r.ok) return null;
  const body = r ? await r.json().catch(() => ({})) : {};
  return body.error || fallback;
};
const cleanNote = (note) =>
  String(note == null ? '' : note)
    .trim()
    .slice(0, NOTE_MAX);

/**
 * @param onChanged     called after a task is completed (lists that do not
 *                      listen to notifyTasksChanged can reload here)
 * @param onHomeUpdated called when the prompt closed the record instead:
 *                      { kind: 'lead', id, leadStatus } or { kind: 'opportunity', id, status }
 *                      (the opportunity editor uses it so a later Save does not
 *                      put the old status back), and again with
 *                      { kind: 'opportunity', id, status, patch } once the Won
 *                      project setup saved the PO details (patch = what it saved)
 */
export function useCompleteTask({ onChanged, onHomeUpdated } = {}) {
  const base = useApiBase();
  const scope = useDataScope();
  const app = useApp();
  const { allAccounts, allProjects, updateProject } = app;
  // The KSA AppContext has no updateAccount (it has no leads); the scope check
  // in finish() means a KSA task never gets as far as needing it.
  const updateAccount = app.updateAccount || null;
  const accountsById = useMemo(() => indexById(allAccounts), [allAccounts]);
  const projectsById = useMemo(() => indexById(allProjects), [allProjects]);
  const [asking, setAsking] = useState(null); // the task while "Complete task" is up
  const [pending, setPending] = useState(null); // { task, home, note } while the prompt is up
  const [notice, setNotice] = useState(''); // a completion that only half worked
  const [wonSetup, setWonSetup] = useState(null); // the opportunity the prompt just closed as Won (Q-02)

  // PUT the completion. Resolves to an error message, or null when it worked.
  const putComplete = useCallback(
    async (task, note) => {
      const text = scope ? '' : cleanNote(note);
      let r = null;
      try {
        r = await fetch(`${base}/crm-actions/${encodeURIComponent(task.id)}`, {
          method: 'PUT',
          headers: JSON_HEADERS,
          body: JSON.stringify(text ? { status: 'complete', note: text } : { status: 'complete' }),
        });
      } catch {
        r = null;
      }
      const err = await errorOf(r, 'Could not complete the task - try again.');
      notifyTasksChanged();
      if (!err && onChanged) onChanged();
      return err;
    },
    [base, scope, onChanged]
  );

  /** Complete with no questions asked (true when it worked). */
  const markComplete = useCallback(async (task, note) => !(await putComplete(task, note)), [putComplete]);

  // The home's open tasks, fresh from the server. The list on screen may be a
  // filtered one (My tasks shows only mine), so it cannot answer "is this the
  // last one?". null = could not tell.
  const openTasksOf = useCallback(
    async (home) => {
      const type = home.kind === 'lead' ? 'account' : TASK_ENTITY;
      try {
        const r = await fetch(`${base}/crm-actions?status=open&entity_type=${type}&entity_id=${encodeURIComponent(home.id)}`);
        const list = r.ok ? await r.json() : null;
        return Array.isArray(list) ? list : null;
      } catch {
        return null;
      }
    },
    [base]
  );

  /** Open "Complete task" for an open task. Nothing is saved until it is confirmed. */
  const complete = useCallback((task) => {
    if (!task || task.status !== 'open') return false;
    setAsking(task);
    return true;
  }, []);

  // "Complete" in the dialog: the next-step check runs AFTER the note, so the
  // prompt (when this is the last open task) comes second and carries the note.
  const finish = useCallback(
    async (note) => {
      const task = asking;
      if (!task) return null;
      const home = scope ? null : homeOfTask(task, accountsById, projectsById);
      if (homeNeedsTask(home, projectsById)) {
        const rows = await openTasksOf(home);
        if (rows && nextStepHome(task, rows, accountsById, projectsById)) {
          setAsking(null);
          setPending({ task, home, note: scope ? '' : cleanNote(note) });
          return null;
        }
      }
      const err = await putComplete(task, note);
      if (err) return err;
      setAsking(null);
      return null;
    },
    [asking, scope, accountsById, projectsById, openTasksOf, putComplete]
  );

  const cancelAsk = useCallback(() => setAsking(null), []);
  const cancel = useCallback(() => setPending(null), []);

  // After the record already has its next step (or was closed), completing the
  // task can still fail. The prompt is gone by then - re-running it would save
  // a second next task - so say so in a notice instead of failing silently.
  const completeAfter = useCallback(
    async (task, note, what) => {
      const err = await putComplete(task, note);
      if (err) setNotice(`${what}, but "${task.description}" is still open: ${err}`);
    },
    [putComplete]
  );

  // Each way out returns an error message, or null when it worked. The task
  // is completed only AFTER the record has its next step (or is closed).
  const saveNext = useCallback(
    async ({ description, owner, due_date, contact_id, tagged }) => {
      if (!pending) return null;
      const { task, home, note } = pending;
      let r = null;
      try {
        r = await fetch(`${base}/crm-actions`, {
          method: 'POST',
          headers: JSON_HEADERS,
          body: JSON.stringify({
            description,
            owner,
            due_date,
            entity_type: home.kind === 'lead' ? 'account' : TASK_ENTITY,
            entity_id: home.id,
            entity_label: home.label,
            ...(contact_id ? { contact_id } : {}),
            ...(tagged && tagged.length ? { tagged } : {}),
          }),
        });
      } catch {
        r = null;
      }
      const err = await errorOf(r, 'Could not save the next task - try again.');
      if (err) return err;
      setPending(null);
      await completeAfter(task, note, 'The next task was saved');
      return null;
    },
    [pending, base, completeAfter]
  );

  const setLeadStatus = useCallback(
    async (leadStatus) => {
      if (!pending) return null;
      const { task, home, note } = pending;
      const res = updateAccount ? await updateAccount(home.id, { leadStatus }) : null;
      if (!res || !res.ok) return (res && res.error) || 'Could not change the status - try again.';
      if (onHomeUpdated) onHomeUpdated({ kind: 'lead', id: home.id, leadStatus });
      setPending(null);
      await completeAfter(task, note, `${home.label} was set to ${leadStatus}`);
      return null;
    },
    [pending, updateAccount, onHomeUpdated, completeAfter]
  );

  const closeOpportunity = useCallback(
    async (status, reason) => {
      if (!pending) return null;
      const { task, home, note } = pending;
      const ok = updateProject ? await updateProject(home.id, reason ? { status, closeReason: reason } : { status }) : false;
      if (!ok) return 'Could not close the opportunity - try again.';
      if (onHomeUpdated) onHomeUpdated({ kind: 'opportunity', id: home.id, status });
      setPending(null);
      await completeAfter(task, note, `${home.label} was marked ${status}`);
      // Q-02: Won asks for the PO here too (the status is already saved)
      if (String(status).toLowerCase() === 'won') {
        setWonSetup({ ...(projectsById.get(String(home.id)) || { id: home.id, name: home.label }), status });
      }
      return null;
    },
    [pending, updateProject, onHomeUpdated, completeAfter, projectsById]
  );

  let prompt = null;
  if (pending) {
    prompt = createElement(NextStepPrompt, {
      task: pending.task,
      home: pending.home,
      note: pending.note,
      onSave: saveNext,
      onSetLeadStatus: setLeadStatus,
      onCloseOpportunity: closeOpportunity,
      onCancel: cancel,
    });
  } else if (asking) {
    prompt = createElement(CompleteTaskDialog, {
      key: asking.id,
      task: asking,
      withNote: !scope,
      onComplete: finish,
      onCancel: cancelAsk,
    });
  } else if (wonSetup) {
    // Into document.body like the prompt: the Dashboard's side column is a size
    // container that would trap the overlay. Keys stop here, so Escape closes
    // this form and not the opportunity editor it may sit on.
    const closeWon = () => setWonSetup(null);
    const form = createElement(
      'div',
      {
        onKeyDown: (e) => {
          e.stopPropagation();
          if (e.key === 'Escape') closeWon();
        },
      },
      createElement(WonProjectSetup, {
        opp: wonSetup,
        onClose: closeWon,
        onSave: async (patch) => {
          const ok = !!(updateProject && (await updateProject(wonSetup.id, patch)));
          // The opportunity editor may be open under this form (its Tasks tab):
          // hand it what was saved, or its next Save would wipe the PO details.
          if (ok && onHomeUpdated) onHomeUpdated({ kind: 'opportunity', id: wonSetup.id, status: wonSetup.status, patch });
          return ok;
        },
      })
    );
    prompt = typeof document !== 'undefined' ? createPortal(form, document.body) : form;
  } else if (notice) {
    prompt = createElement(TaskNotice, { message: notice, onClose: () => setNotice('') });
  }

  return { complete, prompt, markComplete };
}
